import { apiFetch } from "./client";

export type BroadcastTemplate =
  | "data_breach_notification"
  | "privacy_policy_changed"
  | "terms_changed"
  | "maintenance_window"
  | "incident_alert"
  | "incident_resolved"
  | "service_degraded_warning";

export type BroadcastAudience =
  | { readonly type: "all" }
  | {
      readonly type: "affected_user_ids";
      readonly user_ids: ReadonlyArray<string>;
    };

export type AdminBroadcastRecord = {
  readonly broadcast_id: string;
  readonly template: BroadcastTemplate;
  readonly status: string;
  readonly reason: string;
  readonly params: Record<string, string>;
  readonly audience: BroadcastAudience;
  readonly requested_by: string;
  readonly progress: {
    readonly target_count: number;
    readonly enqueued_count: number;
    readonly failed_count: number;
  };
  readonly error: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly completed_at: string | null;
};

export type CreateBroadcastInput = {
  readonly template: BroadcastTemplate;
  readonly reason: string;
  readonly params: Record<string, string>;
  readonly audience: BroadcastAudience;
};

export type CreateBroadcastResult = {
  readonly broadcast_id: string;
  readonly template: BroadcastTemplate;
  readonly status: string;
};

export async function fetchAdminBroadcasts(
  limit = 20,
): Promise<ReadonlyArray<AdminBroadcastRecord>> {
  const res = await apiFetch<{ data: ReadonlyArray<AdminBroadcastRecord> }>(
    `/admin/communications/broadcasts?limit=${limit}`,
  );
  return res.data;
}

export async function fetchAdminBroadcast(
  broadcastId: string,
): Promise<AdminBroadcastRecord> {
  const res = await apiFetch<{ data: AdminBroadcastRecord }>(
    `/admin/communications/broadcasts/${encodeURIComponent(broadcastId)}`,
  );
  return res.data;
}

export async function createAdminBroadcast(
  input: CreateBroadcastInput,
): Promise<CreateBroadcastResult> {
  const path =
    input.template === "data_breach_notification"
      ? "/admin/communications/data-breach"
      : input.template === "privacy_policy_changed"
        ? "/admin/communications/privacy-policy-changed"
        : input.template === "terms_changed"
          ? "/admin/communications/terms-changed"
          : input.template === "maintenance_window"
            ? "/admin/communications/maintenance-window"
            : input.template === "incident_alert"
              ? "/admin/communications/incident-alert"
              : input.template === "incident_resolved"
                ? "/admin/communications/incident-resolved"
                : "/admin/communications/service-degraded";
  const res = await apiFetch<{ data: CreateBroadcastResult }>(path, {
    method: "POST",
    body: JSON.stringify({
      reason: input.reason,
      params: input.params,
      audience: input.audience,
    }),
  });
  return res.data;
}
